import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CoreUtilsService } from '@bepatient/core';
import { select, Store } from '@ngrx/store';
import { SupportedVideoType } from '../dynamic-form-v2/classes/enum-v2';
import { VideoInfoType } from '../dynamic-form-v2/models/dynamic-form-v2.models';
import { selectorCoreRouterUrl } from '../../../../core/src/lib/core-router.reducer';
import { Subscription } from 'rxjs';
import Player from '@vimeo/player';

@Component({
  selector: 'bepatient-video',
  templateUrl: './bepatient-video.component.html',
  styleUrls: ['./bepatient-video.component.scss'],
  standalone: false
})
export class BepatientVideoComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private domSanitizer = inject(DomSanitizer);
  private store$ = inject(Store);
  private coreUtils = inject(CoreUtilsService);

  @Input() video: VideoInfoType;

  @ViewChild('videoFrame', { read: ElementRef, static: false })
  private videoFrame: ElementRef;
  @ViewChild('vimeoPlayer', { read: ElementRef, static: false })
  private vimeoPlayer: ElementRef;

  safeUrl: SafeUrl;
  errorMsg: string;
  thumbnails: any;
  playVideo: boolean;
  videoUrl: string;
  videoId: string;
  subscription: Subscription;
  currentUrl: string;
  isVimeo: boolean;
  player;

  ngOnInit(): void {
    /**
     * Only supported video type can be parsed and passed it to video component
     * Supported video type: YOUTUBE || VIMEO
     */
    if (
      this.video &&
      this.video.videoType &&
      SupportedVideoType[this.video.videoType.toLocaleUpperCase()]
    ) {
      this.videoUrl = this.parsedVideoUrl(this.video);
      this.isVimeo = this.video.videoType === SupportedVideoType.VIMEO;
      if (this.videoUrl) {
        this.getThumbnail(this.video, this.videoId).then(thumbnails => {
          this.thumbnails = thumbnails;
        });
        this.safeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
          this.videoUrl
        );
      }
    } else {
      // TODO: Add toaster or display error msg if required for wrong video type
      // this.errorMsg = 'Video Type Not Supported!';
      console.error(`Error: Not supported video type`);
    }
  }

  ngAfterViewInit(): void {
    // Check if user navigate to another page, stop playing video
    this.subscription = this.store$
      .pipe(select(selectorCoreRouterUrl))
      .subscribe(v => {
        if (
          this.currentUrl &&
          (this.videoFrame || this.player) &&
          this.currentUrl !== v
        ) {
          this.stopVideo();
        }
        this.currentUrl = v;
      });

    // If video is vimeo type, use vimeo library to create a video player
    if (this.isVimeo) {
      // let iframe = document.querySelector('#vimeoPlayer iframe');
      let vimeoOptions = {
        id: this.videoId,
        controls: true,
        quality: '720p' // default 720p, can be adjusted from player controls
        // playsinline: true
      };
      this.player = new Player(this.vimeoPlayer.nativeElement, vimeoOptions);
    }
  }

  /**
   * Method to parse video url based on video type
   */
  parsedVideoUrl(video: VideoInfoType): any {
    this.videoId = video.url.split('/').pop();
    return video.videoType === 'youtube'
      ? `https://www.youtube.com/embed/${this.videoId}?rel=0&enablejsapi=1&version=3&playerapiid=ytplayer`
      : `http://player.vimeo.com/video/${this.videoId}`;
  }

  /**
   * Get thumbnail from video url
   */
  getThumbnail(video: any, id: string): Promise<any> {
    return new Promise(resolve => {
      if (video.videoType == SupportedVideoType.YOUTUBE) {
        resolve({
          thumbnail: `https://i.ytimg.com/vi/${id}/default.jpg`,
          thumbnail2x: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
        });
      } else {
        this.coreUtils
          .getVideoThumbnail(id)
          .then(response => resolve(response))
          .catch(err => {
            // TODO: Add toaster or display error msg if required for wrong video type
            // this.errorMsg = err;
            console.error(err);
          });
      }
    });
  }

  /**
   * Method to play video
   */
  playVideoNow(): void {
    // Setting playVideo true to display video player in DOM
    this.playVideo = true;
    if (this.videoFrame) {
      // This works for only YouTube video
      this.videoFrame.nativeElement.contentWindow.postMessage(
        '{"event":"command","func":"' + 'playVideo' + '","args":""}',
        '*'
      );
    }
    // For VIMEO video
    if (this.isVimeo && this.player) {
      this.player.play();
    }
  }

  /**
   * Method to stop playing video
   */
  stopVideo(): void {
    this.playVideo = false;
    // For YouTube video
    if (this.videoFrame) {
      this.videoFrame.nativeElement.contentWindow.postMessage(
        '{"event":"command","func":"' + 'stopVideo' + '","args":""}',
        '*'
      );
    }
    // For Vimeo Video
    if (this.isVimeo && this.player) {
      this.player.unload();
    }
  }

  ngOnDestroy(): void {
    this.stopVideo();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
